export enum LogLevel {
  ERROR = "error",
  WARN = "warn",
  LOG = "log",
  DEBUG = "debug",
}

// The apps declare `process.env` with their own closed set of keys, so the
// logger reads it through a shape of its own instead of the ambient one.
type ClientEnv = { NODE_ENV?: string; VUE_APP_LOG_LEVEL?: string };

const LEVEL_RANK: Record<LogLevel, number> = {
  [LogLevel.ERROR]: 0,
  [LogLevel.WARN]: 1,
  [LogLevel.LOG]: 2,
  [LogLevel.DEBUG]: 3,
};

let _configured: LogLevel | null = null;

// Escape hatch for hosts whose bundler does not inline `process.env` — a Vite or
// Nuxt app reads its own build flags and states the level here at boot. `null`
// hands the decision back to the environment.
export function setLogLevel(level: LogLevel | null) {
  _configured = level;
}

export default class LoggerService {
  constructor(private name: string) {}

  public debug(...args: unknown[]) {
    _push(LogLevel.DEBUG, this.name, args);
  }

  public log(...args: unknown[]) {
    _push(LogLevel.LOG, this.name, args);
  }

  public warn(...args: unknown[]) {
    _push(LogLevel.WARN, this.name, args);
  }

  public error(...args: unknown[]) {
    _push(LogLevel.ERROR, this.name, args);
  }

  public fatal(report: { message: string; stack: unknown[] }) {
    _push(LogLevel.ERROR, this.name, [report.message, report.stack]);
  }
}

function _push(level: LogLevel, loggerName: string, args: unknown[]) {
  if (LEVEL_RANK[level] > _threshold()) {
    return;
  }

  const texts: string[] = toConsole(loggerName, level, args);
  console[level](...texts);
}

// A production bundle reaches the console with errors only. VUE_APP_LOG_LEVEL
// lifts the ceiling back when a deployed environment has to be debugged.
function _threshold(): number {
  if (_configured) {
    return LEVEL_RANK[_configured];
  }

  const env = _clientEnv();
  const configured = env.VUE_APP_LOG_LEVEL;

  if (configured && configured in LEVEL_RANK) {
    return LEVEL_RANK[configured as LogLevel];
  }

  return env.NODE_ENV === "production"
    ? LEVEL_RANK[LogLevel.ERROR]
    : LEVEL_RANK[LogLevel.DEBUG];
}

// Webpack inlines `process.env` as an object literal but leaves the `process`
// binding itself undefined in the browser, so the read is guarded instead of
// typeof-checked. The cast is what the ambient declarations cannot express: each
// host types `process.env` with its own key set, and none of them describes what
// the bundler actually inlines here.
function _clientEnv(): ClientEnv {
  try {
    return process.env as unknown as ClientEnv;
  } catch {
    return {};
  }
}

function toConsole(
  loggerName: string,
  level: LogLevel,
  messages: unknown[],
): string[] {
  const texts = messages.map(_stringify);
  return [loggerName, level.toUpperCase(), ...texts.filter((_) => _)];
}

function _stringify(o: unknown): string {
  if (o instanceof Error) {
    return o.message;
  }
  if (o instanceof Object) {
    return JSON.stringify(o);
  }

  if (typeof o === "string") {
    return o;
  }

  if (typeof o === "number" || typeof o === "boolean") {
    return o.toString();
  }

  return "";
}
